<template>
  <div
    class="status-card"
    :class="cardClasses"
    :style="cardStyle"
    :aria-label="ariaLabel"
    :data-testid="dataTestid"
    :role="role"
  >
    <div v-if="$slots.icon || icon" class="status-card__icon">
      <slot name="icon">
        <v-icon :style="{ color: iconColor }" :size="iconSize">{{ icon }}</v-icon>
      </slot>
    </div>
    <div class="status-card__content">
      <slot>
        <div v-if="title" class="status-card__title">{{ title }}</div>
        <div v-if="message" class="status-card__message">{{ message }}</div>
      </slot>
    </div>
    <div v-if="$slots.actions" class="status-card__actions">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'

type StatusType = 'success' | 'warning' | 'error' | 'info' | 'neutral'

interface StatusConfig {
  borderColor: string
  backgroundColor: string
  textColor: string
}

const statusConfigs: Record<StatusType, StatusConfig> = {
  success: {
    borderColor: 'var(--success)',
    backgroundColor: 'var(--card)',
    textColor: 'var(--foreground)'
  },
  warning: {
    borderColor: 'var(--warning)',
    backgroundColor: 'var(--card)',
    textColor: 'var(--foreground)'
  },
  error: {
    borderColor: 'var(--danger)',
    backgroundColor: 'var(--card)',
    textColor: 'var(--foreground)'
  },
  info: {
    borderColor: 'var(--info)',
    backgroundColor: 'var(--card)',
    textColor: 'var(--foreground)'
  },
  neutral: {
    borderColor: 'var(--muted-foreground)',
    backgroundColor: 'var(--card)',
    textColor: 'var(--foreground)'
  }
}

export default Vue.extend({
  name: 'StatusCard',

  props: {
    /** Status type determines colours */
    status: {
      type: String as () => StatusType,
      default: 'neutral',
      validator: (value: string) => ['success', 'warning', 'error', 'info', 'neutral'].includes(value)
    },
    /** Optional title */
    title: {
      type: String,
      default: ''
    },
    /** Optional message/description */
    message: {
      type: String,
      default: ''
    },
    /** Icon name (mdi-*) */
    icon: {
      type: String,
      default: ''
    },
    /** Icon size */
    iconSize: {
      type: [Number, String],
      default: 24
    },
    /** Custom icon color (overrides status-based color) */
    customIconColor: {
      type: String,
      default: ''
    },
    /** Apply danger styling to title/value text */
    dangerText: {
      type: Boolean,
      default: false
    },
    /** Custom aria-label */
    ariaLabel: {
      type: String,
      default: ''
    },
    /** Custom data-testid */
    dataTestid: {
      type: String,
      default: ''
    },
    /** ARIA role */
    role: {
      type: String,
      default: 'region'
    },
    /** Card size variant */
    size: {
      type: String as () => 'small' | 'medium' | 'large',
      default: 'medium',
      validator: (value: string) => ['small', 'medium', 'large'].includes(value)
    }
  },

  computed: {
    config(): StatusConfig {
      return statusConfigs[this.status] || statusConfigs.neutral
    },

    cardClasses(): Record<string, boolean> {
      return {
        [`status-card--${this.status}`]: true,
        [`status-card--${this.size}`]: true,
        'status-card--danger-text': this.dangerText
      }
    },

    cardStyle(): Record<string, string> {
      return {
        borderLeftColor: this.config.borderColor,
        backgroundColor: this.config.backgroundColor
      }
    },

    iconColor(): string {
      // If custom color provided, use it; otherwise derive from status
      if (this.customIconColor) {
        return this.resolveIconColor(this.customIconColor)
      }
      // For error status, use danger color; otherwise grey
      if (this.status === 'error') {
        return 'var(--danger)'
      }
      return '#6B7280'
    },

    resolveIconColor() {
      return (color: string): string => {
        const colorMap: Record<string, string> = {
          'error': 'var(--danger)',
          'danger': 'var(--danger)',
          'red': 'var(--danger)',
          'warning': 'var(--warning)',
          'success': 'var(--success)',
          'grey': '#6B7280',
          'gray': '#6B7280'
        }
        return colorMap[color.toLowerCase()] || color
      }
    }
  }
})
</script>

<style scoped>
.status-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: var(--radius-lg);
  border: none; /* No visible border except left accent */
  border-left: 4px solid; /* Left accent stripe */
  transition: var(--transition-base);
}

/* Size variants */
.status-card--small {
  padding: 10px;
}

.status-card--medium {
  padding: 12px;
}

.status-card--large {
  padding: 16px;
}

.status-card__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-card__content {
  flex: 1;
  min-width: 0;
}

.status-card__title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  margin-bottom: 2px;
  line-height: 1.4;
  color: var(--foreground);
  transition: color 0.2s ease;
}

/* Danger state for title text (e.g., numeric values) */
.status-card--danger-text .status-card__title {
  color: var(--danger);
}

.status-card__message {
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: var(--muted-foreground); /* Always neutral grey */
}

.status-card__actions {
  flex-shrink: 0;
}

/* Icon colors are controlled by the iconColor computed property */
.status-card >>> .v-icon {
  transition: color 0.2s ease;
}

/* Hover state for interactive cards */
.status-card:hover {
  box-shadow: var(--shadow-sm);
}
</style>
